import { Suspense, useEffect, useState, useRef, useCallback } from 'react';
import { useSystemConfig } from './lib/system-config';
import { Route, Switch, useLocation, Link } from 'wouter';
import { useAuthStore } from './hooks/useAuth';
import { Login } from './components/auth/Login';
import { Register } from './components/auth/Register';
import { Dashboard } from './pages/Dashboard';
import { WarehouseOnboarding } from './pages/WarehouseOnboarding';
import { DockManagement } from './pages/DockManagement';
import { AppointmentBooking } from './pages/AppointmentBooking';
import { CarrierRequest } from './pages/CarrierRequest';
import LandingPage from './pages/LandingPage';
import { CarrierLogin } from './pages/CarrierLogin';
import { CarrierSignup } from './pages/CarrierSignup';
import { CarrierDashboard } from './pages/CarrierDashboard';
import { AppointmentsTab } from './pages/AppointmentsTab';
import { Analytics } from './pages/Analytics';
import { DwellAnalytics } from './pages/DwellAnalytics';
import { EnvironmentalImpact } from './pages/EnvironmentalImpact';
import { ReceivingExceptions } from './pages/reports/ReceivingExceptions';

import { Orders } from './pages/Orders';
import { Loads } from './pages/Loads';
import { SalesOrders } from './pages/SalesOrders';
import { Carriers } from './pages/Carriers';
import { Customers } from './pages/Customers';
import { CustomerHistory } from './pages/CustomerHistory';
import { BayWhizCopilotWidget } from './components/ai/BayWhizCopilotWidget';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { CarrierForgotPassword } from './pages/CarrierForgotPassword';
import { CarrierResetPassword } from './pages/CarrierResetPassword';
import { CustomerOrders } from './pages/CustomerOrders';
import AIInsights from './pages/AIInsights';
import AdminAccounts from './pages/AdminAccounts';
import Settings from './pages/Settings';
import CarrierSettings from './pages/CarrierSettings';
import { Inbox } from './pages/Inbox';
import { Toaster } from 'react-hot-toast';
import { SplashScreen } from './components/SplashScreen';
import { Sidebar } from './components/layout/Sidebar';
import { Loader2, Menu } from 'lucide-react';
import { POCreationWizard } from './components/POCreationWizard';
import { AppointmentDetailDialog } from './components/appointments/AppointmentDetailDialog';
import ScrollToTop from './components/ScrollToTop';
import { TrialExpiredOverlay } from './components/TrialExpiredOverlay';
import './index.css';

// Marketing pages (eagerly loaded for instant navigation)
import ProductPage from './pages/ProductPage';
import HowItWorksPage from './pages/HowItWorksPage';
import SolutionsPage from './pages/SolutionsPage';
import PricingPage from './pages/PricingPage';
import FaqPage from './pages/FaqPage';
import ContactPage from './pages/ContactPage';
import RequestTrialPage from './pages/RequestTrialPage';
import { PrivacyPolicyPage, TermsOfServicePage } from './pages/LegalDocumentPage';

// AI Dock Coordinator pages (lazy loaded)
import { lazy } from 'react';
const AgentInbox = lazy(() => import('./pages/AgentInbox'));
const AgentActivityLog = lazy(() => import('./pages/AgentActivityLog'));
const AgentRules = lazy(() => import('./pages/AgentRules'));

// ============ BUILD FINGERPRINT ============
// These are compile-time constants injected by Vite
if (typeof window !== 'undefined' && (window.location.search.includes('debug') || window.location.hostname === 'localhost')) {
    console.log('📦 BAYWHIZ_BUILD', { id: __BUILD_ID__, time: __BUILD_TIME__ });
}
// Store in window for debugging access
(window as any).__BAYWHIZ_BUILD__ = { BUILD_ID: __BUILD_ID__, BUILD_TIME: __BUILD_TIME__ };
// ============================================




function ProtectedRoute({ component: Component }: { component: () => JSX.Element | null }) {
    const { isAuthenticated } = useAuthStore();
    const [location, setLocation] = useLocation();

    useEffect(() => {
        if (!isAuthenticated) {
            const isCarrierRoute = location.startsWith('/carrier');
            const returnTo = encodeURIComponent(location + window.location.search);
            const loginPath = isCarrierRoute
                ? `/carrier-login?from=${returnTo}`
                : `/login?from=${returnTo}`;
            setLocation(loginPath);
        }
    }, [isAuthenticated, setLocation, location]);

    if (!isAuthenticated) {
        return null;
    }

    return <Component />;
}

// Holographic Mission Control Loader (Original - Preserved for global use)
function MissionControlLoader() {
    return (
        <div className="flex items-center justify-center h-full bg-slate-900/50 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-4">
                <Loader2 className="w-12 h-12 text-primary animate-spin" />
                <p className="text-primary font-mono text-sm tracking-widest uppercase animate-pulse">Initializing Mission Control...</p>
            </div>
        </div>
    );
}

// Strict Analytics Loader (Batch 15 Compliance)
// Solid background, no opacity, no blur, no pulse on text.
function StrictAnalyticsLoader() {
    return (
        <div className="flex items-center justify-center h-full bg-slate-900 dark:bg-slate-950">
            <div className="flex flex-col items-center gap-4">
                {/* animate-spin is allowed per spec */}
                <Loader2 className="w-12 h-12 text-cyan-500 animate-spin" />
                <p className="text-cyan-500 font-mono text-sm tracking-widest uppercase">Loading Analytics...</p>
            </div>
        </div>
    );
}

// Smart Loader that switches based on route
function RouteAwareSuspenseFallback() {
    const [location] = useLocation();

    // Check if we are in a strict compliance route
    const isStrictRoute = location.startsWith('/analytics') || location.startsWith('/dwell-analytics') || location.startsWith('/reports/');

    if (isStrictRoute) {
        return <StrictAnalyticsLoader />;
    }

    // Marketing pages get a subtle, lightweight fallback (not full-screen to avoid page-reset feel)
    const isMarketingRoute =
        location === '/' ||
        location === '/product' ||
        location === '/how-it-works' ||
        location === '/solutions' ||
        location === '/pricing' ||
        location === '/faq' ||
        location === '/contact' ||
        location === '/request-trial' ||
        location === '/privacy' ||
        location === '/terms';
    if (isMarketingRoute) {
        return (
            <div className="flex items-center justify-center pt-40 pb-20 bg-white">
                <div className="flex items-center gap-3">
                    <Loader2 className="w-5 h-5 text-slate-500 animate-spin" />
                    <span className="text-sm text-slate-500">Loading page…</span>
                </div>
            </div>
        );
    }

    return <MissionControlLoader />;
}

// Route Logic Component to avoid inline hooks
function DashboardRoute() {
    const { user } = useAuthStore();
    if (user?.role === 'carrier') {
        return <ProtectedRoute component={CarrierDashboard} />;
    }
    return <ProtectedRoute component={Dashboard} />;
}

// Manager Only Route Guard
function ManagerRoute({ component: Component }: { component: () => JSX.Element | null }) {
    const { user } = useAuthStore();
    const [, setLocation] = useLocation();

    useEffect(() => {
        if (user && user.role === 'carrier') {
            setLocation('/carrier-dashboard');
        }
    }, [user, setLocation]);

    if (user?.role === 'carrier') {
        return null;
    }

    return <ProtectedRoute component={Component} />;
}

// 404 Not Found Page
function NotFoundPage() {
    const { isAuthenticated, user } = useAuthStore();
    const home = user?.role === 'carrier' ? '/carrier-dashboard' : isAuthenticated ? '/dashboard' : '/';
    return (
        <div className="flex flex-col items-center justify-center h-full gap-4 text-center p-8">
            <h1 className="text-6xl font-bold text-muted-foreground/30">404</h1>
            <p className="text-lg text-muted-foreground">Page not found</p>
            <Link href={home} className="text-primary hover:underline text-sm font-medium">
                ← Back to {isAuthenticated ? 'Dashboard' : 'Home'}
            </Link>
        </div>
    );
}


function StagingBanner() {
    if (typeof window === 'undefined') return null;
    const isStaging = window.location.hostname.includes('run.app') && window.location.hostname.startsWith('baywhiz-staging');
    if (!isStaging) return null;

    return (
        <div className="fixed top-0 left-0 right-0 z-[9999] pointer-events-none flex justify-center">
            <div className="bg-yellow-500 text-black px-4 py-1 text-xs font-bold uppercase tracking-widest shadow-lg rounded-b-md opacity-90 backdrop-blur-sm">
                Staging
            </div>
        </div>
    );
}

function App() {
    const { isAuthenticated, hasHydrated, user } = useAuthStore();
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
    const [location, setLocation] = useLocation();

    // Public paths where the app shell (sidebar, hamburger) must never appear
    const PUBLIC_PATHS = ['/', '/product', '/how-it-works', '/solutions', '/pricing', '/faq', '/contact', '/request-trial', '/privacy', '/terms', '/login', '/register',
        '/carrier-login', '/carrier-signup', '/forgot-password', '/reset-password',
        '/carrier/forgot-password', '/carrier/reset-password', '/guest-book', '/carrier-request'];
    const PROTECTED_APP_PATHS = [
        '/dashboard', '/inbox', '/appointments', '/orders', '/loads', '/sales-orders', '/carriers',
        '/customers', '/docks', '/settings', '/facilities', '/users', '/analytics', '/dwell-analytics',
        '/reports', '/ai-insights', '/admin', '/carrier-dashboard', '/carrier-settings',
        '/agent/inbox', '/agent/activity', '/agent/rules',
    ];
    const isPublicRoute = PUBLIC_PATHS.includes(location);
    const isKnownProtectedRoute = PROTECTED_APP_PATHS.some(
        (p) => location === p || location.startsWith(`${p}/`)
    );

    // Redirect authenticated users away from "/" to their dashboard
    useEffect(() => {
        if (hasHydrated && isAuthenticated && location === '/') {
            const dest = user?.role === 'carrier' ? '/carrier-dashboard' : '/dashboard';
            setLocation(dest);
        }
    }, [hasHydrated, isAuthenticated, location, user?.role, setLocation]);

    // Show app shell only when authenticated AND on a known protected route (never on 404 or public routes)
    const showAppShell = isAuthenticated && !isPublicRoute && isKnownProtectedRoute;

    // THEME: Managed exclusively by useTheme.ts (key: "baywhiz-theme").
    // Do NOT add/remove "dark"/"light" classes here — that creates a split-brain
    // with the canonical useTheme store and causes modals to render the wrong theme.

    // System config is still used for density and legacy migration only.
    const appearance = useSystemConfig(state => state.config.appearance);

    // One-time migration: If legacy system-config has "system", migrate it to "dark".
    const { updateSection } = useSystemConfig();
    useEffect(() => {
        if ((appearance.theme as string) === 'system') {
            updateSection('appearance', { theme: 'dark' });
        }
    }, [appearance.theme, updateSection]);

    useEffect(() => {
        const root = document.documentElement;

        // 2. Density Logic
        // Always clean up both to ensure clean state switching
        root.classList.remove('density-comfortable', 'density-compact');
        root.classList.add(`density-${appearance.density}`);

    }, [appearance.density]);

    // Lock body scroll when mobile sidebar is open
    useEffect(() => {
        if (mobileSidebarOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [mobileSidebarOpen]);

    // Throttled session refresh — prevents request storms across all triggers
    const refreshSession = useAuthStore(state => state.refreshSession);
    const lastRefreshRef = useRef(0);
    const THROTTLE_MS = 30_000; // 30 seconds minimum between refreshes

    const throttledRefresh = useCallback(() => {
        const now = Date.now();
        if (now - lastRefreshRef.current < THROTTLE_MS) return;
        lastRefreshRef.current = now;
        refreshSession();
    }, [refreshSession]);

    // Intercept TRIAL_EXPIRED 403 responses globally
    // After interceptor marks expired, also trigger a canonical refresh
    // so the frontend can re-verify against current DB state.
    useEffect(() => {
        const originalFetch = window.fetch;
        window.fetch = async (...args) => {
            const response = await originalFetch(...args);
            if (response.status === 403) {
                try {
                    const cloned = response.clone();
                    const body = await cloned.json();
                    if (body?.error === 'TRIAL_EXPIRED') {
                        const { user, updateUser } = useAuthStore.getState();
                        const nextStatus = body.subscriptionStatus || 'expired';
                        if (user && user.trialStatus !== nextStatus) {
                            updateUser({
                                ...user,
                                trialStatus: nextStatus,
                                subscriptionStatus: nextStatus,
                                effectiveTier: 'none',
                                trialEndAt: body.trialEndAt ?? user.trialEndAt,
                            });
                        }
                        // Re-verify against canonical DB state (admin may have reset trial)
                        throttledRefresh();
                    }
                } catch { /* non-JSON or parse error — ignore */ }
            }
            return response;
        };
        return () => { window.fetch = originalFetch; };
    }, [throttledRefresh]);

    // Session refresh triggers: boot, tab focus, visibility change, 5-min interval
    useEffect(() => {
        if (!hasHydrated || !isAuthenticated) return;

        // 1. Boot-time refresh
        throttledRefresh();

        // 2. Tab regains visibility
        const onVisibility = () => {
            if (document.visibilityState === 'visible') throttledRefresh();
        };
        document.addEventListener('visibilitychange', onVisibility);

        // 3. Window regains focus
        const onFocus = () => throttledRefresh();
        window.addEventListener('focus', onFocus);

        // 4. Low-frequency background refresh (every 5 min)
        const interval = setInterval(throttledRefresh, 5 * 60_000);

        return () => {
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('focus', onFocus);
            clearInterval(interval);
        };
    }, [hasHydrated, isAuthenticated, throttledRefresh]);

    // Show loading state while Auth Store is rehydrating
    if (!hasHydrated) {
        return <SplashScreen />;
    }

    return (
        <div className="app flex h-screen bg-background text-foreground overflow-hidden">
            <TrialExpiredOverlay />
            <StagingBanner />
            <BayWhizCopilotWidget />
            <Toaster position="bottom-right" />
            {showAppShell && (
                <Sidebar mobileOpen={mobileSidebarOpen} onMobileClose={() => setMobileSidebarOpen(false)} />
            )}

            {/* Mobile backdrop */}
            {mobileSidebarOpen && showAppShell && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 md:hidden"
                    onClick={() => setMobileSidebarOpen(false)}
                />
            )}

            <main className="flex-1 h-screen overflow-auto relative safe-bottom">
                {/* Mobile hamburger (toggle) */}
                {showAppShell && (
                    <button
                        onClick={() => setMobileSidebarOpen(prev => !prev)}
                        className="fixed top-4 left-3 z-30 md:hidden p-2.5 rounded-lg bg-card border border-border text-foreground shadow-lg tap-target"
                        aria-label={mobileSidebarOpen ? 'Close menu' : 'Open menu'}
                    >
                        <Menu size={20} />
                    </button>
                )}
                {/* Scroll to top on route change */}
                <ScrollToTop />
                {/* Use the smart fallback that checks wouter location */}
                <Suspense fallback={<RouteAwareSuspenseFallback />}>
                    <Switch>
                        {/* Public routes */}
                        <Route path="/" component={LandingPage} />
                        <Route path="/product" component={ProductPage} />
                        <Route path="/how-it-works" component={HowItWorksPage} />
                        <Route path="/solutions" component={SolutionsPage} />
                        <Route path="/pricing" component={PricingPage} />
                        <Route path="/faq" component={FaqPage} />
                        <Route path="/contact" component={ContactPage} />
                        <Route path="/request-trial" component={RequestTrialPage} />
                        <Route path="/privacy" component={PrivacyPolicyPage} />
                        <Route path="/terms" component={TermsOfServicePage} />
                        <Route path="/login" component={Login} />
                        <Route path="/register" component={Register} />
                        <Route path="/carrier-login" component={CarrierLogin} />
                        <Route path="/carrier-signup" component={CarrierSignup} />
                        <Route path="/forgot-password" component={ForgotPassword} />
                        <Route path="/reset-password" component={ResetPassword} />
                        <Route path="/carrier/forgot-password" component={CarrierForgotPassword} />
                        <Route path="/carrier/reset-password" component={CarrierResetPassword} />
                        <Route path="/guest-book" component={CarrierRequest} />
                        <Route path="/carrier-request" component={CarrierRequest} />

                        {/* Protected routes */}
                        <Route path="/dashboard" component={DashboardRoute} />

                        <Route path="/carrier-dashboard">
                            {() => <ProtectedRoute component={CarrierDashboard} />}
                        </Route>
                        <Route path="/carrier-settings">
                            {() => <ProtectedRoute component={CarrierSettings} />}
                        </Route>
                        <Route path="/facilities">
                            {() => <ManagerRoute component={WarehouseOnboarding} />}
                        </Route>
                        <Route path="/docks">
                            {() => <ManagerRoute component={DockManagement} />}
                        </Route>
                        <Route path="/appointments/new">
                            {() => <ManagerRoute component={AppointmentBooking} />}
                        </Route>
                        <Route path="/appointments">
                            {() => <ManagerRoute component={AppointmentsTab} />}
                        </Route>
                        <Route path="/orders">
                            {() => <ManagerRoute component={Orders} />}
                        </Route>
                        <Route path="/loads">
                            {() => <ManagerRoute component={Loads} />}
                        </Route>
                        <Route path="/sales-orders">
                            {() => <ManagerRoute component={SalesOrders} />}
                        </Route>

                        <Route path="/analytics">
                            {() => <ManagerRoute component={Analytics} />}
                        </Route>
                        <Route path="/dwell-analytics">
                            {() => <ManagerRoute component={DwellAnalytics} />}
                        </Route>
                        <Route path="/reports/environmental-impact">
                            {() => <ManagerRoute component={EnvironmentalImpact} />}
                        </Route>
                        <Route path="/reports/receiving-exceptions">
                            {() => <ManagerRoute component={ReceivingExceptions} />}
                        </Route>
                        <Route path="/carriers">
                            {() => <ManagerRoute component={Carriers} />}
                        </Route>
                        <Route path="/customers">
                            {() => <ManagerRoute component={Customers} />}
                        </Route>
                        <Route path="/customers/:id/history">
                            {() => <ManagerRoute component={CustomerHistory} />}
                        </Route>
                        <Route path="/customers/:id/orders">
                            {() => <ManagerRoute component={CustomerOrders} />}
                        </Route>
                        <Route path="/inbox">
                            {() => <ManagerRoute component={Inbox} />}
                        </Route>
                        <Route path="/settings">
                            {() => <ManagerRoute component={Settings} />}
                        </Route>
                        <Route path="/ai-insights">
                            {() => <ManagerRoute component={AIInsights} />}
                        </Route>
                        <Route path="/admin">
                            {() => <ManagerRoute component={AdminAccounts} />}
                        </Route>

                        {/* AI Dock Coordinator routes */}
                        <Route path="/agent/inbox">
                            {() => <ManagerRoute component={() => <AgentInbox />} />}
                        </Route>
                        <Route path="/agent/activity">
                            {() => <ManagerRoute component={() => <AgentActivityLog />} />}
                        </Route>
                        <Route path="/agent/rules">
                            {() => <ManagerRoute component={() => <AgentRules />} />}
                        </Route>

                        {/* Catch-all 404 */}
                        <Route component={NotFoundPage} />
                    </Switch>
                </Suspense>
            </main>

            {/* Global Modals */}
            <POCreationWizard />
            <AppointmentDetailDialog />
        </div>
    );
}

export default App;
